import React, { useEffect, useMemo, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import ForestOutlinedIcon from "@mui/icons-material/ForestOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import default_theme from "assets/themes/themes";
import methodologyImage from "assets/images/RuractiveStepsMethodology.png";
import rddMobilityIcon from "assets/icons/RDD_icons_version_2-outline/mobility_outline500px.png";
import rddClimateIcon from "assets/icons/RDD_icons_version_2-outline/climate_outline500px.png";
import rddAgriFoodIcon from "assets/icons/RDD_icons_version_2-outline/agri-food_outline500px.png";
import rddTourismIcon from "assets/icons/RDD_icons_version_2-outline/tourism_outline500px.png";
import rddCultureIcon from "assets/icons/RDD_icons_version_2-outline/culture_outline500px.png";
import rddLocalIcon from "assets/icons/RDD_icons_version_2-outline/local_outline500px.png";
import styles from "./ProjectDetailPage.module.css";
import RIE_ROLES from "../../../constants/rieRoles";
import RieInfoCard from "../../../components/RieRoles/RieInfoCard";
import {
  fetchProjectDetailRequest,
  fetchProjectInformationRequest,
  fetchUserPermissionRequest,
} from "../../../redux/projects/projectsSlice";
import {
  fetchProjectDynamosRequest,
} from "../../../redux/projectDynamos/projectDynamosSlice";

const HANDBOOK_URL = "/documents/HANDBOOK_Del4.1_4.pdf";

const JOURNEY_STEPS = [
  {
    step: 0,
    title: "Getting Started",
    desc: "Define your starting point",
  },
  {
    step: 1,
    title: "Stakeholder Identification",
    desc: "Identify & prioritise stakeholders",
  },
  {
    step: 2,
    title: "Stakeholder Engagement",
    desc: "Communicate and involve stakeholders",
  },
  {
    step: 3,
    title: "Stakeholder Empowerment",
    desc: "Strengthen stakeholder participation",
  },
];

const GUIDE_PRINCIPLES = [
  {
    icon: GroupOutlinedIcon,
    title: "Participation",
    desc: "Involve a wide range of local stakeholders.",
  },
  {
    icon: VolunteerActivismOutlinedIcon,
    title: "Inclusion",
    desc: "Consider groups at risk of exclusion.",
  },
  {
    icon: PublicOutlinedIcon,
    title: "Place-based",
    desc: "Start from the specific rural territory.",
  },
];

const RURAL_DEVELOPMENT_DRIVERS = [
  { name: "Sustainable Multimodal Mobility", icon: rddMobilityIcon },
  { name: "Energy Transition & Climate Neutrality", icon: rddClimateIcon },
  {
    name: "Sustainable Agri-Food Systems & Ecosystem Management",
    icon: rddAgriFoodIcon,
  },
  { name: "Nature-Based & Cultural Tourism", icon: rddTourismIcon },
  { name: "Culture & Cultural Innovation", icon: rddCultureIcon },
  { name: "Local Services, Health & Well-Being", icon: rddLocalIcon },
];

const CROSS_CUTTING_PRIORITIES = [
  {
    icon: PeopleAltOutlinedIcon,
    title: "Social justice & inclusion",
    tagline: "Promote equal opportunities and participation.",
    desc: "Ensure that all citizens have the opportunities and resources to participate fully in economic, social and cultural life.",
  },
  {
    icon: ForestOutlinedIcon,
    title: "Biodiversity",
    tagline: "Protect and value the diversity of life.",
    desc: "Recognise and protect the variety of ecosystems, species and genes and the ecosystem services they provide.",
  },
  {
    icon: DeviceThermostatOutlinedIcon,
    title: "Climate change mitigation & adaptation",
    tagline: "Reduce climate impacts and build resilience.",
    desc: "Address climate change by reducing its causes and adapting to its actual or expected effects.",
  },
];

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentProjectDetail,
    currentProjectInformation,
    detailLoading,
    detailError,
    informationLoading,
    informationError,
  } = useSelector((state) => state.projects);

  const authState = useSelector((state) => state.auth);
  const currentUserId = authState?.user?.id;

  const isOwner =
    currentProjectDetail &&
    currentProjectDetail.owner_id &&
    String(currentProjectDetail.owner_id) === String(currentUserId);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
      dispatch(fetchProjectInformationRequest({ projectId }));
      dispatch(fetchUserPermissionRequest({ projectId }));
      dispatch(fetchProjectDynamosRequest({ projectId }));
    }
  }, [projectId, dispatch]);

  const isAccessDenied = useMemo(() => {
    return (
      (detailError &&
        (detailError.includes("403") || detailError.toLowerCase().includes("not allowed"))) ||
      (informationError &&
        (informationError.includes("403") || informationError.toLowerCase().includes("not allowed")))
    );
  }, [detailError, informationError]);

  useEffect(() => {
    if (isAccessDenied) {
      const timer = setTimeout(() => {
        navigate("/workspaces");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isAccessDenied, navigate]);

  const handleBackClick = () => {
    navigate("/workspaces");
  };

  if (
    (detailLoading && !currentProjectDetail) ||
    (informationLoading && !currentProjectInformation)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAccessDenied) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            p: 4,
          }}
        >
          <Alert
            severity="warning"
            icon={false}
            sx={{
              mb: 3,
              width: "100%",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#856404" }}>
              Access Denied
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: "#856404" }}>
              You no longer have access to this project.
            </Typography>
            <Typography variant="body2" sx={{ color: "#856404", textAlign: "left" }}>
              This may be because:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 2, textAlign: "left", pl: 3, color: "#856404" }}>
              <li>You were removed from the project</li>
              <li>Your access permissions were changed</li>
              <li>The project was deleted or made private</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic", color: "#856404" }}>
              You will be redirected to your projects in a few seconds...
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={handleBackClick}
            sx={{
              backgroundColor: default_theme.palette.other.chatGreen,
              "&:hover": {
                backgroundColor: default_theme.palette.other.chatGreenHover,
              },
              mt: 2,
              px: 4,
            }}
          >
            Go to My Projects
          </Button>
        </Box>
      </Container>
    );
  }

  if (detailError || informationError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{detailError || informationError}</Alert>
        <Button onClick={handleBackClick} sx={{ mt: 2 }}>
          Back to Workspaces
        </Button>
      </Container>
    );
  }

  const project = currentProjectDetail;

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5">Project not found</Typography>
        <Button onClick={handleBackClick} sx={{ mt: 2 }}>
          Back to Workspaces
        </Button>
      </Container>
    );
  }

  const rdds = project.rdds || [];
  const rie = project.rie;
  const stakeholders = project.stakeholders || [];

  // Basic progress derived from existing project data.
  const stepStatus = [
    rdds.length > 0 ||
      Boolean(rie && (rie.coordinator || rie.sub_coordinator || (rie.sites && rie.sites.length > 0))),
    stakeholders.length > 0,
    stakeholders.some((s) => s.is_local_task_force_member),
    false,
  ];

  const currentStepIndex = stepStatus.findIndex((done) => !done);
  const allStepsCompleted = currentStepIndex === -1;
  const continueStep = allStepsCompleted ? 3 : currentStepIndex;

  const openHandbook = () => {
    window.open(HANDBOOK_URL, "_blank");
  };

  const goToStep = (step) => {
    navigate(`/workspaces/projects/${projectId}/step-${step}`);
  };

  return (
    <Box className={styles.pageContainer}>
      <Container maxWidth="lg" className={styles.container}>
        {/* Top bar */}
        <Box className={styles.topBar}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            className={styles.backButton}
          >
            Back to projects
          </Button>
          {isOwner && (
            <IconButton
              onClick={() => navigate(`/workspaces/projects/${projectId}/settings`)}
              className={styles.settingsButton}
              title="Project Settings"
            >
              <SettingsIcon />
            </IconButton>
          )}
        </Box>

        {/* Header */}
        <Box className={styles.header}>
          <Typography className={styles.eyebrow}>Project</Typography>
          <Typography variant="h3" className={styles.title}>
            {project.title}
          </Typography>

          {project.framework && (
            <Chip
              label={project.framework.name}
              className={styles.frameworkChip}
            />
          )}

          {project.description && (
            <Box className={styles.descriptionCard}>
              <Typography variant="body1" className={styles.description}>
                {project.description}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Your RURACTIVE journey */}
        <Box className={styles.journeySection}>
          <Typography variant="h5" className={styles.sectionTitle}>
            Your RURACTIVE journey
          </Typography>
          <Typography variant="body1" className={styles.sectionText}>
            This project follows the RURACTIVE methodology to establish and strengthen
            a Rural Innovation Ecosystem around the development priorities relevant to
            your territory.
          </Typography>

          <Box className={styles.journeyRow}>
            {JOURNEY_STEPS.map((step, index) => (
              <Fragment key={step.step}>
                {index > 0 && (
                  <ArrowForwardOutlinedIcon className={styles.journeyArrow} />
                )}
                <Box
                  onClick={() => goToStep(step.step)}
                  className={styles.stepCard}
                >
                  <Typography className={styles.stepBadge}>STEP {step.step}</Typography>
                  <Typography variant="h6" className={styles.stepName}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" className={styles.stepDesc}>
                    {step.desc}
                  </Typography>
                  <Button
                    variant="contained"
                    className={styles.openButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToStep(step.step);
                    }}
                  >
                    Open
                  </Button>
                </Box>
              </Fragment>
            ))}
          </Box>
        </Box>

        {/* How the RURACTIVE process works */}
        <Box className={styles.section}>
          <Typography variant="h5" className={styles.sectionTitle}>
            How the RURACTIVE process works
          </Typography>
          <Typography variant="body1" className={styles.sectionText}>
            The RURACTIVE methodology guides you step by step through building a
            Rural Innovation Ecosystem. The Handbook provides inclusive, step-by-step
            guidelines you can follow at your own pace.
          </Typography>

          <Box className={styles.handbookCard}>
            <Box className={styles.handbookIcon}>
              <MenuBookOutlinedIcon className={styles.handbookIconInner} />
            </Box>
            <Typography variant="h6" className={styles.handbookTitle}>
              Inclusive Step by Step Guidelines
            </Typography>
            <Button
              variant="outlined"
              className={styles.handbookButton}
              onClick={openHandbook}
            >
              View handbook
            </Button>
          </Box>

          <Box className={styles.methodologyImageWrap}>
            <img
              src={methodologyImage}
              alt="RURACTIVE methodology path"
              className={styles.methodologyImage}
            />
          </Box>
        </Box>

        {/* What guides the process */}
        <Box className={styles.section}>
          <Typography variant="h5" className={styles.sectionTitle}>
            What guides the process?
          </Typography>
          <Grid container spacing={2.5}>
            {GUIDE_PRINCIPLES.map((principle) => {
              const Icon = principle.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={principle.title}>
                  <Box className={styles.guideCard}>
                    <Box className={styles.guideIcon}>
                      <Icon />
                    </Box>
                    <Typography variant="h6" className={styles.guideTitle}>
                      {principle.title}
                    </Typography>
                    <Typography variant="body2" className={styles.guideDesc}>
                      {principle.desc}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Rural development drivers */}
        <Box className={styles.section}>
          <Typography variant="h5" className={styles.sectionTitle}>
            Rural Development Drivers
          </Typography>
          <Typography variant="h6" className={styles.rddHeading}>
            What are you working on?
          </Typography>
          <Typography variant="body1" className={styles.sectionText}>
            RURACTIVE organises the development of the Rural Innovation
            Ecosystem around one or more Rural Development Drivers (RDDs).
            These drivers represent the main areas of rural development
            addressed through the methodology.
          </Typography>
          <Grid container spacing={2.5}>
            {RURAL_DEVELOPMENT_DRIVERS.map((rdd) => (
              <Grid item xs={12} sm={6} key={rdd.name}>
                <Box className={styles.rddCard}>
                  <img
                    src={rdd.icon}
                    alt={rdd.name}
                    className={styles.rddIcon}
                  />
                  <Typography className={styles.rddName}>
                    {rdd.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* The people of the RIE */}
        <Box className={styles.section}>
          <Typography variant="h5" className={styles.sectionTitle}>
            The people of the RIE
          </Typography>
          <Typography variant="body1" className={styles.sectionText}>
            A Rural Innovation Ecosystem brings together people and organisations
            with different roles and contributions.
          </Typography>

          <Box className={styles.rolesGrid}>
            {RIE_ROLES.map((role) => (
              <RieInfoCard key={role.title} item={role} />
            ))}
          </Box>
        </Box>

        {/* Cross-cutting priorities */}
        <Box className={styles.section}>
          <Typography variant="h5" className={styles.sectionTitle}>
            Cross-cutting priorities
          </Typography>
          <Grid container spacing={2.5}>
            {CROSS_CUTTING_PRIORITIES.map((priority) => {
              const Icon = priority.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={priority.title}>
                  <Box className={styles.guideCard}>
                    <Box className={styles.guideIcon}>
                      <Icon />
                    </Box>
                    <Typography variant="h6" className={styles.guideTitle}>
                      {priority.title}
                    </Typography>
                    <Typography className={styles.guideTagline}>
                      {priority.tagline}
                    </Typography>
                    <Typography variant="body2" className={styles.guideDesc}>
                      {priority.desc}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ProjectDetailPage;
